import React from 'react';
import { CustomModal } from '../../../../common/CustomModal';
import { CheckCircle, XCircle, X, ArrowLeft } from 'lucide-react';
import { toast } from 'react-toastify';
import { transactionAction } from '../services/WalletTransactionServices';
import '../../../../../styles/components/action-modal.scss';
import StatusBadge from '../../../../common/StatusBadge';
import { TRANSACTION_STATUS } from '../../../../../constants/status';

export class ActionModal extends React.Component {
  constructor() {
    super();
    this.state = {
      submitting: false,
      rejectReason: '',
      showRejectInput: false,
      rejectError: '',
    };
  }

  handleApprove = async () => {
    const { selectedPayment, onComplete } = this.props;

    try {
      this.setState({ submitting: true });
      const payload = {
        id: selectedPayment.id,
        action: 'approve',
      };

      const response = await transactionAction(payload);
      if (response.success) {
        toast.success(response.message || 'Payment approved successfully');
        onComplete?.();
        this.handleClose();
      } else {
        toast.error(response.message || 'Failed to approve payment');
      }
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong');
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleReject = async () => {
    const { selectedPayment, onComplete } = this.props;
    const { rejectReason } = this.state;

    // Validation
    if (!rejectReason.trim()) {
      this.setState({ rejectError: 'Reject reason is required' });
      return;
    }

    try {
      this.setState({ submitting: true, rejectError: '' });
      const payload = {
        id: selectedPayment.id,
        action: 'reject',
        reason: rejectReason.trim(),
      };

      const response = await transactionAction(payload);
      if (response.success) {
        toast.success(response.message || 'Payment rejected successfully');
        onComplete?.();
        this.handleClose();
      } else {
        toast.error(response.message || 'Failed to reject payment');
      }
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong');
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleRejectClick = () => {
    this.setState({ showRejectInput: true });
  };

  handleClose = () => {
    this.setState({
      rejectReason: '',
      showRejectInput: false,
      rejectError: '',
      submitting: false,
    });
    this.props.onClose();
  };

  render() {
    const { show, selectedPayment } = this.props;
    const { rejectReason, submitting, showRejectInput, rejectError } = this.state;

    if (!selectedPayment) return null;

    return (
      <CustomModal isOpen={show} header={`Payment Action Required`} onClose={this.handleClose}>
        <div className="action-modal-container">
          {/* Payment Details Section */}
          <div className="payment-details-card">
            <h4 className="section-title">Payment Details</h4>
            <div className="details-grid">
              <div className="detail-item-new">
                <span className="detail-label">Transaction ID</span>
                <span className="detail-value">{selectedPayment.transaction_id || '-'}</span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">Method</span>
                <span className="detail-value">{selectedPayment.method || '-'}</span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">Amount</span>
                <span className="detail-value amount">
                  {parseFloat(selectedPayment.credit || 0).toFixed(3)}
                </span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">Status</span>
                <span className={`detail-value  `}>
                  <StatusBadge status={TRANSACTION_STATUS[selectedPayment.status]} />
                </span>
              </div>
              {selectedPayment.auction && (
                <div className="detail-item-new">
                  <span className="detail-label">Auction ID</span>
                  <span className="detail-value">MZAD {selectedPayment.auction}</span>
                </div>
              )}
            </div>
          </div>

          {/* Reject Reason Section - Shows when reject is clicked */}
          {showRejectInput && (
            <div className="reject-section">
              <label htmlFor="rejectReason" className="reject-label">
                Reason for Rejection <span className="required">*</span>
              </label>
              <textarea
                id="rejectReason"
                className={`reject-textarea ${rejectError ? 'error' : ''}`}
                value={rejectReason}
                onChange={(e) =>
                  this.setState({
                    rejectReason: e.target.value,
                    rejectError: e.target.value.trim() ? '' : 'Reject reason is required',
                  })
                }
                placeholder="Enter the reason for rejecting this payment..."
                rows={4}
                disabled={submitting}
              />
              {rejectError && <span className="error-message">{rejectError}</span>}
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-buttons-container">
            {!showRejectInput ? (
              <>
                <button
                  className="btn-new btn-action"
                  onClick={this.handleClose}
                  disabled={submitting}
                >
                  <X size={18} />
                  Cancel
                </button>

                <button
                  className="btn-new btn-new--danger"
                  onClick={this.handleRejectClick}
                  disabled={submitting}
                >
                  <XCircle size={18} />
                  Reject
                </button>

                <button
                  className="btn-new btn-new--success"
                  onClick={this.handleApprove}
                  disabled={submitting}
                >
                  <CheckCircle size={18} />
                  {submitting ? 'Approving...' : 'Approve'}
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn-new btn-action"
                  onClick={() =>
                    this.setState({ showRejectInput: false, rejectReason: '', rejectError: '' })
                  }
                  disabled={submitting}
                >
                  <ArrowLeft size={18} />
                  Back
                </button>

                <button
                  className="btn-new btn-new--danger"
                  onClick={this.handleReject}
                  disabled={submitting || !rejectReason.trim()}
                >
                  <XCircle size={18} />
                  {submitting ? 'Rejecting...' : 'Confirm Reject'}
                </button>
              </>
            )}
          </div>
        </div>
      </CustomModal>
    );
  }
}
